// AGC: SelectCrop - Step 1: Choose crop for lot management plan
import React, { useState, useMemo } from 'react'
import { ArrowLeft, Search } from 'lucide-react'
import { GlassCard } from '../shared'
import { CROP_TYPES, type CropType, type Screen } from '../../types'
import { useCustomList } from '../../hooks/useCustomList'
import { STORAGE_KEY_CUSTOM_CROP_TYPES } from '../settings/CulturasSettings'

interface SelectCropProps {
  plotId: string
  onBack: () => void
  onNavigate: (screen: Screen, data?: any) => void
}

/**
 * AGC: Mock function to get suggested crops based on region and season
 * AGC: Simulates EMBRAPA/INTA recommendations
 */
const getSuggestedCrops = (): CropType[] => {
  // AGC: Mock logic - in real app, would use plot location and current date
  const currentMonth = new Date().getMonth() + 1 // 1-12
  
  // AGC: Simple seasonal logic for Brazil (mock)
  if (currentMonth >= 10 || currentMonth <= 2) {
    // AGC: Oct-Feb: Soja season
    return ['soja', 'milho']
  } else if (currentMonth >= 3 && currentMonth <= 5) {
    // AGC: Mar-May: Second crop
    return ['milho', 'trigo']
  } else {
    // AGC: Jun-Sep: Winter crops
    return ['trigo', 'cafe']
  }
}

/**
 * AGC: SelectCrop - Choose crop for management plan
 */
export const SelectCrop: React.FC<SelectCropProps> = ({ plotId, onBack, onNavigate }) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [showOtherInput, setShowOtherInput] = useState(false)
  const [customCropName, setCustomCropName] = useState('')

  const suggestedCrops = getSuggestedCrops()

  // AGC: Merge built-in crop types with user-defined ones from localStorage
  const customCropTypes = useCustomList(STORAGE_KEY_CUSTOM_CROP_TYPES)
  const mergedCropTypes = useMemo(
    () => [
      ...CROP_TYPES.map((c) => ({ id: c.id as string, name: c.name, icon: c.icon, color: c.color })),
      ...customCropTypes.map((c) => ({ id: c.id, name: c.label, icon: '🌿', color: '#10B981' })),
    ],
    [customCropTypes]
  )

  // AGC: Filter all crops by search (merged list)
  const filteredCrops = mergedCropTypes.filter(crop =>
    crop.name.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // AGC: Handle crop selection
  const handleSelectCrop = (cropType: CropType, cropName: string) => {
    onNavigate('management-plan', {
      plotId,
      cropType,
      cropName,
    })
  }

  // AGC: Handle custom crop
  const handleCustomCrop = () => {
    const trimmed = customCropName.trim()
    if (!trimmed) {
      alert('Por favor, insira o nome do cultivo')
      return
    }
    handleSelectCrop('outros', trimmed)
  }

  return (
    <div className="min-h-screen bg-[#F4F5F7]">
      {/* AGC: Header */}
      <header className="bg-[#0B5F4A] px-4 pt-4 pb-6 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-white mb-4"
        >
          <ArrowLeft className="w-5 h-5" />
          Voltar
        </button>
        
        <h1 className="text-white text-2xl font-bold">Selecione o cultivo</h1>
        <p className="text-emerald-200 text-sm mt-2">
          Escolha o cultivo para gerar o plano de manejo
        </p>
      </header>

      <div className="px-4 py-4 space-y-4">
        {/* AGC: Suggested Crops Section */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 px-1">
            Sugeridos para esta região e época
          </h2>
          
          <div className="space-y-2">
            {suggestedCrops.map(cropId => {
              const crop = CROP_TYPES.find(c => c.id === cropId)
              if (!crop) return null
              
              return (
                <button
                  key={crop.id}
                  onClick={() => handleSelectCrop(crop.id, crop.name)}
                  className="w-full bg-white rounded-lg shadow-md p-4 border-2 border-emerald-200 hover:border-emerald-500 hover:shadow-lg transition-all active:scale-[0.98] flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{crop.icon}</span>
                    <span className="text-lg font-semibold text-gray-900">{crop.name}</span>
                  </div>
                  <span className="text-emerald-600 font-medium">Recomendado</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Search Bar */}
        <div className="relative pt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar cultivo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-10 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#0B5F4A] focus:ring-2 focus:ring-[#0B5F4A]/20"
          />
        </div>

        {/* AGC: All Crops Section */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3 px-1">
            Todos os cultivos
          </h2>
          
          <div className="space-y-2">
            {filteredCrops.map(crop => {
              const isSuggested = suggestedCrops.includes(crop.id as CropType)
              if (isSuggested) return null // AGC: Don't duplicate suggested crops

              return (
                <button
                  key={crop.id}
                  onClick={() => handleSelectCrop(crop.id as CropType, crop.name)}
                  className="w-full bg-white rounded-lg shadow-md p-4 border-2 border-gray-200 hover:border-[#0B5F4A] hover:shadow-lg transition-all active:scale-[0.98] flex items-center gap-3"
                >
                  <span className="text-2xl">{crop.icon}</span>
                  <span className="text-base font-medium text-gray-900">{crop.name}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* AGC: Other Crop Option */}
        <div className="pt-2">
          {!showOtherInput ? (
            <button
              onClick={() => setShowOtherInput(true)}
              className="w-full bg-gray-100 rounded-lg p-4 border-2 border-dashed border-gray-400 hover:border-[#0B5F4A] hover:bg-gray-50 transition-all"
            >
              <span className="text-base font-medium text-gray-700">+ Outro cultivo</span>
            </button>
          ) : (
            <GlassCard>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Outro cultivo (digite o nome)
              </label>
              <input
                type="text"
                value={customCropName}
                onChange={(e) => setCustomCropName(e.target.value)}
                placeholder="Ex: Feijão, Algodão..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#0B5F4A] focus:ring-2 focus:ring-[#0B5F4A]/20 mb-3"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowOtherInput(false)
                    setCustomCropName('')
                  }}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCustomCrop}
                  className="flex-1 px-4 py-2 bg-[#0B5F4A] rounded-lg text-white font-medium hover:bg-[#094935]"
                >
                  Confirmar
                </button>
              </div>
            </GlassCard>
          )}
        </div>
      </div>
    </div>
  )
}

export default SelectCrop


